import React from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { DatePicker } from '../../DatePicker';
import { formatPeriodLabel, formatPeriodMonthTitle } from '../../../utils/employeeRoutes';

type EmployeePayrollPeriodPickerProps = {
  from: string;
  to: string;
  onShift: (direction: -1 | 1) => void;
  onApplyCustomPeriod: (range: { from: string; to: string }) => void;
  showLabel?: boolean;
};

export function EmployeePayrollPeriodPicker({
  from,
  to,
  onShift,
  onApplyCustomPeriod,
  showLabel = true,
}: EmployeePayrollPeriodPickerProps) {
  const [rangeOpen, setRangeOpen] = React.useState(false);
  const [draftFrom, setDraftFrom] = React.useState(from);
  const [draftTo, setDraftTo] = React.useState(to);
  const [rangeError, setRangeError] = React.useState<string | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!rangeOpen) return;
    setDraftFrom(from);
    setDraftTo(to);
    setRangeError(null);
  }, [rangeOpen, from, to]);

  React.useEffect(() => {
    if (!rangeOpen) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setRangeOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [rangeOpen]);

  const applyRange = () => {
    if (!draftFrom || !draftTo) {
      setRangeError('Bitte Start- und Enddatum wählen.');
      return;
    }
    if (draftFrom > draftTo) {
      setRangeError('Das Startdatum muss vor dem Enddatum liegen.');
      return;
    }
    onApplyCustomPeriod({ from: draftFrom, to: draftTo });
    setRangeOpen(false);
    setRangeError(null);
  };

  const periodSubtitle = formatPeriodLabel(from, to)
    .split(' – ')
    .map((p) => p.trim())
    .join(' – ');

  const rangePanel =
    rangeOpen && triggerRef.current ? (
      <div
        ref={panelRef}
        className="fixed z-[200] rounded-lg border border-app-border bg-app-surface p-4 shadow-lg"
        style={{
          top: triggerRef.current.getBoundingClientRect().bottom + 6,
          left: triggerRef.current.getBoundingClientRect().left,
          width: 320,
          maxWidth: 'min(360px, calc(100vw - 24px))',
        }}
        role="dialog"
        aria-label="Zeitraum wählen"
      >
        <p className="mb-3 text-xs font-semibold text-app-text">Zeitraum</p>
        <div className="grid grid-cols-1 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-app-text-muted">Von</span>
            <DatePicker value={draftFrom} onChange={setDraftFrom} className="mt-1 w-full" />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-app-text-muted">Bis</span>
            <DatePicker value={draftTo} onChange={setDraftTo} className="mt-1 w-full" />
          </label>
        </div>
        {rangeError ? <p className="mt-2 text-xs text-red-600">{rangeError}</p> : null}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setRangeOpen(false)}
            className="rounded-md border border-app-border px-3 py-1.5 text-xs font-medium text-app-text-muted hover:bg-app-hover"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={applyRange}
            className="rounded-md bg-pipedrive-blue px-3 py-1.5 text-xs font-semibold text-white hover:opacity-95"
          >
            Übernehmen
          </button>
        </div>
      </div>
    ) : null;

  return (
    <>
      {showLabel ? (
        <span className="mr-0.5 text-xs font-medium text-app-text-muted">
          Abrechnungszeitraum
        </span>
      ) : null}
      <div className="inline-flex items-center overflow-hidden rounded-md border border-app-border bg-app-surface">
        <button
          type="button"
          onClick={() => onShift(-1)}
          className="inline-flex h-7 w-7 items-center justify-center border-r border-app-border text-app-text-muted hover:bg-app-hover"
          aria-label="Vorheriger Zeitraum"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setRangeOpen((v) => !v)}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-semibold text-app-text hover:bg-app-hover"
          aria-expanded={rangeOpen}
          aria-haspopup="dialog"
        >
          <Calendar className="h-3.5 w-3.5 shrink-0 text-app-text-subtle" />
          <span>{formatPeriodMonthTitle(from)}</span>
          <span className="text-xs font-normal text-app-text-subtle">· {periodSubtitle}</span>
        </button>
        <button
          type="button"
          onClick={() => onShift(1)}
          className="inline-flex h-7 w-7 items-center justify-center border-l border-app-border text-app-text-muted hover:bg-app-hover"
          aria-label="Nächster Zeitraum"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
      {rangeOpen && typeof document !== 'undefined' ? createPortal(rangePanel, document.body) : null}
    </>
  );
}
